import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Skeleton, Switch, Tooltip, Typography } from 'antd';
import type { InputRef } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import { useXoeConversations } from '../../hooks/xoe/useXoe';
import { EmptyState } from '../shared';
import XoeMark from './XoeMark';
import XoeConversationRailRow from './XoeConversationRailRow';
import { useXoeSize } from './XoeSurface';
import { buildRailGroups } from '../../utils/xoe/groupConversations';
import type { XoeContainerSize, XoeConversationRailProps } from '../../types/xoe/XoeUiTypes';

const { Text } = Typography;

const SEARCH_DEBOUNCE_MS = 300;

const XoeConversationRail: React.FC<XoeConversationRailProps> = ({
	activeUuid,
	focusPatientId,
	collapsed,
	onToggleCollapsed,
	onSelect,
	onNewChat,
	onRename,
	onArchive,
	onDelete,
	undoUuid,
	onUndoArchive,
}) => {
	const size = useXoeSize();
	const overlay = size === 'compact';

	const [searchInput, setSearchInput] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [showAll, setShowAll] = useState(false);
	const searchRef = useRef<InputRef>(null);

	useEffect(() => {
		const timer = setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	// Both pieces of search state, or the old term stays live for one debounce on the next record.
	useEffect(() => {
		setShowAll(false);
		setSearchInput('');
		setSearchTerm('');
	}, [focusPatientId]);

	// A container that shrinks into compact must not suddenly cover the thread with an open overlay.
	const prevSizeRef = useRef<XoeContainerSize | null>(null);
	useEffect(() => {
		const prev = prevSizeRef.current;
		prevSizeRef.current = size;
		if (prev !== null && prev !== 'compact' && size === 'compact' && !collapsed) onToggleCollapsed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [size]);

	const searching = searchTerm.trim() !== '';

	// Never pass the focused patient: the server filter excludes NULLs, so General could never appear.
	const { data: conversations, isLoading } = useXoeConversations(undefined, searchTerm);

	if (collapsed && overlay) return null;

	const onRailKeyDown = (e: React.KeyboardEvent) => {
		if (overlay && e.key === 'Escape') {
			e.stopPropagation();
			onToggleCollapsed();
		}
	};

	if (collapsed) {
		return (
			<aside className="mg-xoe-rail" data-testid="xoe-rail" data-collapsed="true" data-overlay="false">
				<div className="mg-xoe-rail-icons" data-testid="xoe-conversation-rail">
					<Tooltip title="Show conversations" placement="right">
						<Button
							type="text"
							size="small"
							icon={<MenuUnfoldOutlined />}
							onClick={onToggleCollapsed}
							aria-label="Show conversations"
							data-testid="xoe-rail-toggle"
						/>
					</Tooltip>
					<Tooltip title="New conversation" placement="right">
						<Button
							type="text"
							size="small"
							icon={<PlusOutlined />}
							onClick={onNewChat}
							aria-label="New conversation"
							data-testid="xoe-rail-new-conversation"
						/>
					</Tooltip>
					<Tooltip title="Search conversations" placement="right">
						<Button
							type="text"
							size="small"
							icon={<SearchOutlined />}
							onClick={() => {
								onToggleCollapsed();
								window.setTimeout(() => searchRef.current?.focus(), 0);
							}}
							aria-label="Search conversations"
						/>
					</Tooltip>
				</div>
			</aside>
		);
	}

	const { groups, sectionedButEmpty } = buildRailGroups({
		conversations: conversations ?? [],
		focusPatientId,
		activeUuid,
		showAll,
		searching,
	});

	const state = isLoading ? 'loading' : (conversations?.length ?? 0) === 0 ? 'empty' : 'populated';

	const body = () => {
		if (isLoading) {
			return (
				<div className="mg-xoe-rail-skeleton">
					{[0, 1, 2].map((i) => (
						<Skeleton.Input key={i} active size="small" block />
					))}
				</div>
			);
		}

		if (!conversations || conversations.length === 0) {
			// Static copy: the term is often a patient's name and must never be echoed on screen.
			return searching ? (
				<div className="mg-xoe-rail-notice">No matching conversations</div>
			) : (
				<EmptyState
					icon={
						<span className="mg-xoe-rail-empty-mark">
							<XoeMark size={32} animated={false} />
						</span>
					}
					title="No conversations yet"
					description="Start one with New conversation."
				/>
			);
		}

		if (sectionedButEmpty) {
			return <div className="mg-xoe-rail-notice">No conversations for this patient yet</div>;
		}

		return groups.map((group) => (
			<div key={group.key} data-testid={group.testId} data-group={group.groupSlug}>
				{group.heading && <div className="mg-xoe-rail-group-heading">{group.heading}</div>}
				{group.items.map((conv) => (
					<XoeConversationRailRow
						key={conv.uuid}
						conversation={conv}
						isActive={conv.uuid === activeUuid}
						onSelect={onSelect}
						onRename={onRename}
						onArchive={onArchive}
						onDelete={onDelete}
					/>
				))}
			</div>
		));
	};

	return (
		<>
			<aside
				className="mg-xoe-rail"
				data-testid="xoe-rail"
				data-collapsed="false"
				data-overlay={String(overlay)}
				onKeyDown={onRailKeyDown}
			>
				<div className="mg-xoe-rail-head">
					<div className="mg-xoe-rail-head-row">
						<Button
							type="primary"
							block
							icon={<PlusOutlined />}
							onClick={onNewChat}
							data-testid="xoe-rail-new-conversation"
						>
							New conversation
						</Button>
						<Tooltip title="Hide conversations">
							<Button
								type="text"
								icon={<MenuFoldOutlined />}
								onClick={onToggleCollapsed}
								aria-label="Hide conversations"
								data-testid="xoe-rail-toggle"
							/>
						</Tooltip>
					</div>

					<Input
						ref={searchRef}
						allowClear
						prefix={<SearchOutlined className="mg-xoe-rail-search-icon" />}
						placeholder="Search conversations"
						aria-label="Search conversations"
						data-testid="xoe-conversation-search"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>

					{/* Inline undo, not a toast: useNotifications has no action slot. */}
					{undoUuid && (
						<div className="mg-xoe-rail-undo">
							<Text type="secondary">Conversation archived</Text>
							<Button type="link" size="small" onClick={onUndoArchive} data-testid="xoe-conversation-archive-undo">
								Undo
							</Button>
						</div>
					)}

					{/* Absent while searching: a search has already left the sectioning this escapes. */}
					{focusPatientId !== null && !searching && (
						<div className="mg-xoe-rail-all">
							<Text type="secondary">All conversations</Text>
							<Switch
								size="small"
								checked={showAll}
								onChange={setShowAll}
								aria-label="Show all conversations"
								data-testid="xoe-all-conversations-toggle"
							/>
						</div>
					)}
				</div>

				<div className="mg-xoe-rail-scroll" data-testid="xoe-conversation-list" data-state={state}>
					{body()}
				</div>
			</aside>

			{overlay && <div className="mg-xoe-rail-scrim" data-testid="xoe-rail-scrim" onClick={onToggleCollapsed} aria-hidden />}
		</>
	);
};

export default XoeConversationRail;
